import type { CSSProperties, ReactNode } from 'react';
import { color, font } from '../theme.js';

/**
 * The shared HUD frame every screen sits inside: radial background, faint grid,
 * scanlines, corner brackets. `container-type: size` is what lets all the
 * `cqmin` sizing inside a screen scale as one unit from 390px to 1120px.
 */
export function Screen({
  children,
  brackets = 'all',
  style,
}: {
  children: ReactNode;
  brackets?: 'all' | 'diagonal' | 'top';
  style?: CSSProperties;
}) {
  return (
    <div
      style={{
        containerType: 'size',
        position: 'relative',
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        background: `radial-gradient(120% 120% at 50% 0%,#0d150c 0%,#070b07 58%,${color.bgDeep} 100%)`,
        fontFamily: font.display,
        color: color.text,
        ...style,
      }}
    >
      <div className="grid-bg" aria-hidden />
      <div className="scanlines" aria-hidden />
      {(brackets === 'all' || brackets === 'diagonal') && <div className="bracket tl" aria-hidden />}
      {(brackets === 'all' || brackets === 'top') && <div className="bracket tr" aria-hidden />}
      {brackets === 'all' && <div className="bracket bl" aria-hidden />}
      {(brackets === 'all' || brackets === 'diagonal') && <div className="bracket br" aria-hidden />}
      {children}
    </div>
  );
}

/** The lime "S" logo mark (SkillIssueClub), matching the wordmark and favicon. */
export function LogoMark({ size = 40 }: { size?: number }) {
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: size * 0.22,
        background: `linear-gradient(150deg,${color.limeBright},${color.limeDeep})`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: color.limeInk,
        font: `800 ${size * 0.55}px ${font.mono}`,
        boxShadow: `0 0 ${size * 0.5}px rgba(166,255,46,.4)`,
        flex: 'none',
      }}
    >
      S
    </div>
  );
}

/** Two-letter avatar tile. Lime for you, cyan for the opponent. */
export function Avatar({
  name,
  side = 'you',
  size = 40,
}: {
  name: string;
  side?: 'you' | 'opponent';
  size?: number;
}) {
  const isYou = side === 'you';
  const accent = isYou ? '166,255,46' : '34,224,255';
  const fg = isYou ? color.limeBright : color.cyanBright;
  const initials = name.replace(/[^a-zA-Z0-9]/g, '').slice(0, 2).toUpperCase() || '??';
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: size * 0.2,
        background: `linear-gradient(150deg,rgba(${accent},.25),rgba(${accent},.07))`,
        border: `1px solid rgba(${accent},.55)`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 700,
        fontSize: size * 0.38,
        color: fg,
        boxShadow: `0 0 ${size * 0.35}px rgba(${accent},.2)`,
        flex: 'none',
        letterSpacing: '.02em',
      }}
    >
      {initials}
    </div>
  );
}

/** The primary lime CTA, with the mockups' slow glow pulse. */
export function PrimaryButton({
  children,
  onClick,
  disabled,
  style,
  glow = true,
}: {
  children: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  style?: CSSProperties;
  glow?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '14px 20px',
        borderRadius: 12,
        background: `linear-gradient(160deg,${color.limeBright},${color.limeDeep})`,
        color: color.limeInk,
        fontWeight: 700,
        fontSize: 16,
        letterSpacing: '.05em',
        animation: glow && !disabled ? 'ctaGlow 2.2s ease-in-out infinite' : undefined,
        ...style,
      }}
    >
      {children}
    </button>
  );
}

export function GhostButton({
  children,
  onClick,
  tone = 'neutral',
  style,
}: {
  children: ReactNode;
  onClick?: () => void;
  tone?: 'neutral' | 'lime' | 'danger';
  style?: CSSProperties;
}) {
  const tones = {
    neutral: { border: 'rgba(234,255,234,.18)', bg: 'rgba(255,255,255,.03)', fg: 'rgba(234,255,234,.8)' },
    lime: { border: 'rgba(166,255,46,.3)', bg: 'rgba(166,255,46,.06)', fg: color.limeBright },
    danger: { border: 'rgba(255,46,77,.32)', bg: 'rgba(255,46,77,.06)', fg: color.redBright },
  } as const;
  const t = tones[tone];
  return (
    <button
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '13px 20px',
        border: `1px solid ${t.border}`,
        borderRadius: 12,
        background: t.bg,
        color: t.fg,
        fontWeight: 700,
        fontSize: 15,
        letterSpacing: '.04em',
        ...style,
      }}
    >
      {children}
    </button>
  );
}

/** Small uppercase caption with wide tracking — used everywhere in the mockups. */
export function Label({
  children,
  color: c = 'rgba(234,255,234,.45)',
  size = 11,
  tracking = '.22em',
}: {
  children: ReactNode;
  color?: string;
  size?: number;
  tracking?: string;
}) {
  return (
    <span style={{ fontSize: size, letterSpacing: tracking, fontWeight: 600, color: c }}>
      {children}
    </span>
  );
}

export function Mono({
  children,
  style,
}: {
  children: ReactNode;
  style?: CSSProperties;
}) {
  return <span style={{ fontFamily: font.mono, ...style }}>{children}</span>;
}

/** Three bouncing dots, for "searching" / "waiting" states. */
export function Dots({ tint = color.lime }: { tint?: string }) {
  return (
    <span style={{ display: 'inline-flex', gap: 3, alignItems: 'center' }}>
      {[0, 0.2, 0.4].map((d) => (
        <span
          key={d}
          style={{
            width: 6,
            height: 6,
            borderRadius: '50%',
            background: tint,
            animation: `dots 1.2s ease-in-out infinite ${d}s`,
          }}
        />
      ))}
    </span>
  );
}
